import { API_BASE } from '../env'
import { request } from './http'
import type { Refine, Schema } from './schema'

// ── Settings profiles ────────────────────────────────────────────────────────────────────────────
//
// One model for everything a device should be: a settings profile holds sections ("security",
// "network.openwrt", …), applies at a scope, and is versioned. Baselines (one global, one per brand)
// and per-store or per-device exceptions are profiles too, with a `role` that says so. The relay
// omits null fields, so every `| null` field here may be absent.

export type SettingFieldType = 'bool' | 'int' | 'text' | 'choice' | 'list' | 'object' | 'schedule'

/**
 * - `list_key`: A list merges item by item on this; composite keys join with `+`
 *   ("package+permission").
 */
export type SettingFieldWire = Refine<
  Schema<'SettingFieldWire'>,
  {
    type: SettingFieldType
  }
>

/**
 * - `printer_models`: The "printer" category only: per model, the settings it may manage. Models
 *   not listed can't be managed.
 */
export type SectionKindWire = Refine<
  Schema<'SectionKindWire'>,
  {
    disruption: 'none' | 'brief' | 'reboot'
    drift_action: 'reassert' | 'report_only'
    fields: SettingFieldWire[]
  }
>

/**
 * What a profile is for. The type decides which categories it may hold, which devices it reaches,
 * how it is listed and which editor it opens in.
 */
export type ProfileType = 'blueprint' | 'router' | 'agent' | 'printer' | 'firmware' | 'windows_update' | 'maintenance_window' | 'agent_version'

/**
 * - `fields`: The fields of the category this type may hold; null means every field.
 */
export type ProfileTypeCategoryWire = Schema<'ProfileTypeCategoryWire'>

/**
 * - `label`: "Router template".
 * - `plural`: "Router templates".
 * - `platforms`: With `reaches: "platforms"`, the platforms it reaches (`linux`, `windows`…); null
 *   otherwise.
 */
export type ProfileTypeWire = Refine<
  Schema<'ProfileTypeWire'>,
  {
    type: ProfileType
    /** `platforms`: only devices whose agent reports one of `platforms` (an unknown platform is never reached). */
    reaches: 'routers' | 'not_routers' | 'all' | 'platforms'
  }
>

export type SectionKindsWire = Refine<
  Schema<'SectionKindsWire'>,
  {
    kinds: SectionKindWire[]
    /** In the order they are listed. */
    profile_types: ProfileTypeWire[]
  }
>

export function getSectionKinds(): Promise<SectionKindsWire> {
  return request<SectionKindsWire>('GET', `${API_BASE}/section-kinds`)
}

export type ProfileRole = 'baseline' | 'profile' | 'exception'

/** `{ "<kind>": { "<key>": value } }`. Sensitive values come back as "••••". */
export type SectionsWire = Record<string, Record<string, unknown>>

export type PolicyAppliesToWire = Schema<'PolicyAppliesToWire'>

/**
 * - `type_label`: "Router template".
 */
export type PolicyWire = Refine<
  Schema<'PolicyWire'>,
  {
    role: ProfileRole
    type: ProfileType
  }
>

export type PolicyDetailWire = Refine<
  Schema<'PolicyDetailWire'>,
  {
    policy: PolicyWire
    draft?: SectionsWire | null
    published?: SectionsWire | null
  }
>

export interface PolicyQuery {
  role?: ProfileRole
  type?: ProfileType
  brandId?: string
  includeArchived?: boolean
}

export function getPolicies(query: PolicyQuery = {}): Promise<{ policies: PolicyWire[] }> {
  const params = new URLSearchParams()
  if (query.role) params.set('role', query.role)
  if (query.type) params.set('type', query.type)
  if (query.brandId) params.set('brand_id', query.brandId)
  if (query.includeArchived) params.set('include_archived', 'true')
  return request<{ policies: PolicyWire[] }>('GET', `${API_BASE}/policies?${params.toString()}`)
}

export function createPolicy(body: {
  name: string
  description?: string | null
  brand_id?: string | null
  role?: 'profile' | 'baseline'
  type: ProfileType
}): Promise<PolicyWire> {
  return request<PolicyWire>('POST', `${API_BASE}/policies`, { json: body })
}

/** A brand's baseline of this type (`null` = the Global one), created on first use. */
export function getOrCreateBaseline(brandId: string | null, type: ProfileType): Promise<PolicyWire> {
  return request<PolicyWire>('POST', `${API_BASE}/baselines`, {
    json: { brand_id: brandId, type },
  })
}

/** A store's or device's exceptions of this type, created on first use. */
export function getOrCreateExceptions(
  scopeKind: 'location' | 'device',
  scopeId: string,
  type: ProfileType,
): Promise<PolicyWire> {
  return request<PolicyWire>('POST', `${API_BASE}/exceptions`, {
    json: { scope_kind: scopeKind, scope_id: scopeId, type },
  })
}

export function getPolicy(policyId: string): Promise<PolicyDetailWire> {
  return request<PolicyDetailWire>('GET', `${API_BASE}/policies/${policyId}`)
}

export function updatePolicy(
  policyId: string,
  body: { name: string; description?: string | null },
): Promise<PolicyDetailWire> {
  return request<PolicyDetailWire>('PUT', `${API_BASE}/policies/${policyId}`, { json: body })
}

/** Archives it; a baseline can't be removed. */
export function archivePolicy(policyId: string): Promise<Schema<'SuccessWire'>> {
  return request<Schema<'SuccessWire'>>('DELETE', `${API_BASE}/policies/${policyId}`)
}

export type PolicyVersionWire = Refine<
  Schema<'PolicyVersionWire'>,
  {
    state: 'draft' | 'published'
  }
>

export function getPolicyVersions(policyId: string): Promise<{ versions: PolicyVersionWire[] }> {
  return request<{ versions: PolicyVersionWire[] }>('GET', `${API_BASE}/policies/${policyId}/versions`)
}

export type PolicyVersionDetailWire = Refine<
  Schema<'PolicyVersionDetailWire'>,
  {
    state: 'draft' | 'published'
    sections?: SectionsWire
  }
>

export function getPolicyVersion(policyId: string, version: number): Promise<PolicyVersionDetailWire> {
  return request<PolicyVersionDetailWire>('GET', `${API_BASE}/policies/${policyId}/versions/${version}`)
}

/** 400 lists every problem in one sentence when the settings can't be applied as written. */
/** What the relay does about each setting once a device has it: `{kind: {key: mode}}`. */
export type EnforcementWire = Record<string, Record<string, string>>

/**
 * Saves the draft.
 *
 * `enforcement` left out keeps whatever the draft already had — a caller that predates per-setting
 * enforcement never wipes a choice somebody made.
 */
export function savePolicyDraft(
  policyId: string,
  sections: SectionsWire,
  enforcement?: EnforcementWire,
): Promise<PolicyDetailWire> {
  return request<PolicyDetailWire>('PUT', `${API_BASE}/policies/${policyId}/draft`, {
    json: enforcement ? { sections, enforcement } : { sections },
  })
}

export function discardPolicyDraft(policyId: string): Promise<PolicyDetailWire> {
  return request<PolicyDetailWire>('DELETE', `${API_BASE}/policies/${policyId}/draft`)
}

/**
 * `release: true` also rolls it out everywhere it applies, now (an all-at-once rollout, whose id comes
 * back) — which needs policy.apply as well. A change to server-only settings (maintenance windows) is
 * in effect as soon as it's published, whatever `release` says, and `released` comes back true.
 */
export function publishPolicy(
  policyId: string,
  body: { change_summary?: string | null; release: boolean },
): Promise<Schema<'PolicyPublishedWire'>> {
  return request<Schema<'PolicyPublishedWire'>>('POST', `${API_BASE}/policies/${policyId}/publish`, {
    json: body,
  })
}

/** Makes an earlier published version the live one again — how a change is undone. */
export function releasePolicyVersion(policyId: string, version: number): Promise<Schema<'PolicyReleasedWire'>> {
  return request<Schema<'PolicyReleasedWire'>>('POST', `${API_BASE}/policies/${policyId}/release`, {
    json: { version },
  })
}

export type PolicyAssignmentWire = Schema<'PolicyAssignmentRecord'>

/**
 * - `settings`: "kind.key".
 */
export type AssignmentConflictWire = Schema<'AssignmentConflictWire'>

export function getPolicyAssignments(policyId: string): Promise<Schema<'PolicyAssignmentsWire'>> {
  return request<Schema<'PolicyAssignmentsWire'>>('GET', `${API_BASE}/policies/${policyId}/assignments`)
}

export function assignPolicy(
  policyId: string,
  body: { scope_kind: string; scope_id?: string | null; priority: number },
): Promise<Schema<'PolicyAssignedWire'>> {
  return request<Schema<'PolicyAssignedWire'>>(
    'POST',
    `${API_BASE}/policies/${policyId}/assignments`,
    { json: body },
  )
}

export function updatePolicyAssignment(assignmentId: string, priority: number): Promise<Schema<'SuccessWire'>> {
  return request<Schema<'SuccessWire'>>('PUT', `${API_BASE}/policy-assignments/${assignmentId}`, {
    json: { priority },
  })
}

export function deletePolicyAssignment(assignmentId: string): Promise<Schema<'SuccessWire'>> {
  return request<Schema<'SuccessWire'>>('DELETE', `${API_BASE}/policy-assignments/${assignmentId}`)
}

export type ImpactChangeWire = Schema<'ImpactChangeWire'>

export type PolicyImpactWire = Schema<'PolicyImpactWire'>

/** How many devices and stores the profile reaches, and with `sections`, what would change on a few. */
export function getPolicyImpact(policyId: string, sections?: SectionsWire): Promise<PolicyImpactWire> {
  return request<PolicyImpactWire>('POST', `${API_BASE}/policies/${policyId}/impact`, {
    json: sections ? { sections } : {},
  })
}

/** Where one value came from. */
export interface SetByWire {
  policy_id: string
  policy_name: string
  role: ProfileRole
  version: number
  scope_kind: string
  scope_id?: string | null
  assignment_id: string
}

/** Paths are "key", "list[itemKey]", or for the router "area/sub/key". */
export type ProvenanceWire = Record<string, SetByWire>

export type PreviewKindWire = Refine<
  Schema<'PreviewKindWire'>,
  {
    desired?: Record<string, unknown>
    provenance?: ProvenanceWire
    problems?: string[] | null
  }
>

/** A device's settings as they would be with these sections in place of the profile's own. */
export function previewPolicy(
  policyId: string,
  deviceId: string,
  sections: SectionsWire,
): Promise<{ kinds: PreviewKindWire[] }> {
  return request<{ kinds: PreviewKindWire[] }>('POST', `${API_BASE}/policies/${policyId}/preview`, {
    json: { device_id: deviceId, sections },
  })
}
